import React from "react";
import { formatChatTimestamp } from "../studyGroupHubConfig";

const HubChatDrawer = ({
  isOpen,
  onClose,
  group,
  user,
  messages = [],
  setMessages,
  memberAvatarMap = {},
  replyingTo,
  setReplyingTo,
  messageInput,
  setMessageInput,
  sending = false,
  sendMessage,
  scrollToMessage,
  onSelectMessage,
  onSelectChallenge,
  messagesEndRef,
  chatInputRef,
  groupId,
  deleteStudyGroupMessage,
  Swal,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl w-full max-w-2xl h-[85vh] md:h-[80vh] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 border border-gray-100">
        {/* Chat Header */}
        <div className="shrink-0 relative flex justify-between items-center px-6 py-4 bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center backdrop-blur-md">
              <i className="fa-solid fa-comments text-base"></i>
            </div>
            <div>
              <h3 className="text-base font-bold leading-tight">Group Chat</h3>
              <p className="text-xs text-blue-100">{group?.name || "Study Room"}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white transition flex items-center justify-center"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {/* Chat Messages Stream */}
        <div className="flex-1 overflow-y-auto p-5 md:p-6 flex flex-col items-start gap-4 bg-slate-50/50">
          {messages.length === 0 ? (
            <div className="text-center my-auto mx-auto text-gray-400 py-10">
              <i className="fa-solid fa-message text-3xl mb-2 text-gray-300"></i>
              <p className="text-sm font-bold text-gray-600">No messages yet</p>
              <p className="text-xs text-gray-400">Say hello and start collaborating!</p>
            </div>
          ) : null}

          {messages.map((msg) => {
            const senderName =
              `${msg?.sender?.firstName || ""} ${msg?.sender?.lastName || ""}`.trim() ||
              "Member";
            const isCode = msg?.type === "reviewer_code";
            const isChallenge = msg?.type === "challenge";
            const isAdmin = String(msg?.sender?._id) === String(group?.owner);
            const isMe = user && String(msg?.sender?._id) === String(user._id);
            const resolvedAvatarUrl =
              memberAvatarMap[String(msg?.sender?._id)] ||
              msg?.sender?.avatar ||
              `https://api.dicebear.com/6.x/initials/svg?seed=${encodeURIComponent(senderName)}`;

            const replySenderName = msg?.replyTo
              ? `${msg.replyTo?.sender?.firstName || ""} ${
                  msg.replyTo?.sender?.lastName || ""
                }`.trim() || "Member"
              : "";
            const isReplySenderMe = user && String(msg?.replyTo?.sender?._id) === String(user._id);
            const replySnippet = msg?.replyTo
              ? msg.replyTo?.type === "reviewer_code"
                ? msg.replyTo?.reviewer?.topic || msg.replyTo?.text || "Shared Reviewer"
                : msg.replyTo?.text || ""
              : "";

            return (
              <div
                key={msg._id}
                id={`msg-${msg._id}`}
                className={`flex gap-3 max-w-[90%] md:max-w-[80%] transition-all duration-300 ${
                  isMe ? "self-end flex-row-reverse" : "self-start"
                } ${isCode ? "opacity-95" : ""}`}
              >
                <img
                  src={resolvedAvatarUrl}
                  alt={senderName}
                  className="w-9 h-9 rounded-full bg-white border border-gray-200 object-cover shrink-0 mt-0.5 shadow-xs"
                />
                <div className={`flex-1 flex flex-col ${isMe ? "items-end" : "items-start"}`}>
                  <div
                    className={`flex items-baseline gap-1.5 mb-1 ${
                      isMe ? "flex-row-reverse" : ""
                    }`}
                  >
                    <p className="text-xs font-bold text-gray-800">{isMe ? "You" : senderName}</p>
                    {isAdmin && (
                      <span className="text-[9px] bg-amber-50 text-amber-700 border border-amber-200 px-1 rounded font-bold">
                        Admin
                      </span>
                    )}
                    <span
                      className="text-[10px] text-gray-400"
                      title={new Date(msg.createdAt).toLocaleString()}
                    >
                      {formatChatTimestamp(msg.createdAt)}
                    </span>
                  </div>

                  <div className="relative group/msg inline-block max-w-full">
                    {/* Quoted Message */}
                    {msg.replyTo && (
                      <div
                        onClick={() => scrollToMessage(msg.replyTo._id)}
                        className={`cursor-pointer mb-1 px-3 py-1.5 rounded-xl border-l-[3px] text-xs max-w-full transition-all flex flex-col gap-0.5 ${
                          isMe
                            ? "bg-blue-100 border-blue-600 text-gray-800 ml-auto items-end"
                            : "bg-gray-100 border-blue-600 text-gray-800 mr-auto items-start"
                        }`}
                        title="Jump to original message"
                      >
                        <div className="flex items-center gap-1 font-bold text-[11px] text-blue-700">
                          <i className="fa-solid fa-reply text-[9px]"></i>
                          <span>{isReplySenderMe ? "You" : replySenderName}</span>
                        </div>
                        <p className="truncate max-w-xs text-[11px] text-gray-600">{replySnippet}</p>
                      </div>
                    )}

                    {isChallenge ? (
                      <div
                        onClick={() => onSelectChallenge && onSelectChallenge(msg)}
                        className={`bg-gradient-to-r from-violet-50 to-indigo-50 border border-indigo-200/80 rounded-2xl p-3.5 inline-block cursor-pointer hover:shadow-md transition ${
                          isMe ? "rounded-tr-none" : "rounded-tl-none"
                        }`}
                      >
                        <div className="flex items-center gap-1.5 text-[10px] text-indigo-700 font-extrabold uppercase tracking-wide mb-1">
                          <i className="fa-solid fa-bolt text-amber-500"></i>
                          <span>Group Challenge</span>
                        </div>
                        <p className="text-sm font-bold text-gray-900">{msg.text}</p>
                        <span className="text-[10px] font-black px-2 py-0.5 rounded-lg border inline-flex items-center gap-1 text-indigo-700 bg-white border-indigo-200 mt-2 shadow-2xs">
                          <i className="fa-solid fa-bolt text-amber-500 text-[10px]"></i>
                          <span>View Challenge</span>
                        </span>
                      </div>
                    ) : isCode ? (
                      <div
                        onClick={() => onSelectMessage(msg)}
                        className={`bg-white border rounded-2xl p-3.5 inline-block cursor-pointer hover:shadow-md transition ${
                          isMe
                            ? "border-blue-200 shadow-sm rounded-tr-none"
                            : "border-gray-200 shadow-sm rounded-tl-none"
                        }`}
                      >
                        <p className="text-[10px] text-blue-600 font-extrabold uppercase tracking-wide mb-1">
                          Shared Reviewer
                        </p>
                        <p className="text-sm font-bold text-gray-900">
                          {msg?.reviewer?.topic || msg?.text?.replace("Shared Reviewer: ", "")}
                        </p>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded border inline-flex items-center gap-1 text-blue-700 bg-blue-50 border-blue-100 mt-2">
                          <i className="fa-solid fa-share-nodes text-[9px]"></i> View Reviewer
                        </span>
                      </div>
                    ) : (
                      <div
                        className={`shadow-xs rounded-2xl p-3 text-sm break-words leading-relaxed ${
                          isMe
                            ? "bg-blue-600 text-white rounded-tr-none"
                            : "bg-white border border-gray-200 text-gray-800 rounded-tl-none"
                        }`}
                      >
                        {msg?.text || ""}
                      </div>
                    )}

                    {/* Floating Action Buttons */}
                    <div
                      className={`absolute top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover/msg:opacity-100 transition-opacity z-10 ${
                        isMe ? "-left-16 flex-row-reverse" : "-right-16 flex-row"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setReplyingTo(msg)}
                        className="w-6 h-6 rounded-full bg-white hover:bg-blue-50 text-gray-500 hover:text-blue-600 border border-gray-200 shadow-xs flex items-center justify-center transition"
                        title="Reply"
                      >
                        <i className="fa-solid fa-reply text-[10px]"></i>
                      </button>

                      {isMe && (
                        <button
                          type="button"
                          onClick={async () => {
                            const result = await Swal.fire({
                              title: "Unsend Message?",
                              text: "This message will be removed for everyone in this study group.",
                              icon: "warning",
                              showCancelButton: true,
                              confirmButtonColor: "#EF4444",
                              cancelButtonColor: "#6B7280",
                              confirmButtonText: "Unsend",
                            });
                            if (result.isConfirmed) {
                              try {
                                await deleteStudyGroupMessage(groupId, msg._id);
                                setMessages((prev) => prev.filter((m) => m._id !== msg._id));
                              } catch (err) {
                                Swal.fire({
                                  icon: "error",
                                  title: "Error",
                                  text: err.response?.data?.message || "Failed to unsend message.",
                                  confirmButtonColor: "#2563EB",
                                });
                              }
                            }
                          }}
                          className="w-6 h-6 rounded-full bg-white hover:bg-red-50 text-gray-500 hover:text-red-500 border border-gray-200 shadow-xs flex items-center justify-center transition"
                          title="Unsend"
                        >
                          <i className="fa-solid fa-trash text-[10px]"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Replying Banner */}
        {replyingTo && (
          <div className="px-5 py-2 bg-blue-50 border-t border-blue-100 flex items-center justify-between gap-3 text-xs shrink-0">
            <div className="flex items-center gap-2 overflow-hidden">
              <i className="fa-solid fa-reply text-blue-600"></i>
              <span className="font-bold text-gray-800">
                Replying to{" "}
                {String(replyingTo?.sender?._id) === String(user?._id)
                  ? "yourself"
                  : `${replyingTo?.sender?.firstName || ""} ${
                      replyingTo?.sender?.lastName || ""
                    }`.trim() || "Member"}
              </span>
              <span className="text-gray-400 truncate">
                {replyingTo?.type === "reviewer_code"
                  ? replyingTo?.reviewer?.topic || "Shared Reviewer"
                  : replyingTo?.text || ""}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setReplyingTo(null)}
              className="w-5 h-5 rounded-full hover:bg-gray-200 text-gray-500 flex items-center justify-center"
            >
              <i className="fa-solid fa-xmark text-[10px]"></i>
            </button>
          </div>
        )}

        {/* Chat Input */}
        <div className="p-4 bg-white border-t border-gray-100 flex gap-2 shrink-0">
          <input
            ref={chatInputRef}
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") sendMessage();
              if (e.key === "Escape") setReplyingTo(null);
            }}
            placeholder={replyingTo ? "Write a reply..." : "Type your message..."}
            className="flex-1 px-4 py-3 rounded-2xl border border-gray-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none bg-gray-50 focus:bg-white text-sm transition font-medium"
          />
          <button
            type="button"
            disabled={sending || !messageInput.trim()}
            onClick={sendMessage}
            className="px-5 py-3 rounded-2xl font-bold text-white bg-blue-600 hover:bg-blue-700 transition flex items-center justify-center disabled:bg-blue-300 disabled:cursor-not-allowed shadow-sm"
          >
            {sending ? (
              <i className="fa-solid fa-circle-notch fa-spin text-sm"></i>
            ) : (
              <i className="fa-solid fa-paper-plane text-sm"></i>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HubChatDrawer;
